import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { MuiTelInput } from 'mui-tel-input';

import AddressAutocomplete from '@/components/address-autocomplete';
import Button from '@/components/Button';
import Modal from '@/components/Modals/Modal';

import { AUTO_CREATE_LOGISTIC_MODES_OPTIONS, PLATFORM_NAME } from './constants';
import useMLParcelConfig from './useMLParcelConfig';

interface MLParcelConfigProps {
  isOpen: boolean;
  onClose: () => void;
  name: string;
  pluginId: string;
  clientId: string;
  activeMlCount: number;
  webhookPlatformOwner: string | null;
  onSaved?: () => void;
}

const MLParcelConfig = (props: MLParcelConfigProps) => {
  const theme = useTheme();
  const hooks = useMLParcelConfig(props);

  const onSave = async () => {
    const success = await hooks.onSavePluginConfig();
    if (success) {
      props.onSaved?.();
      props.onClose();
    }
  };

  const isPrimaryDisabled =
    Boolean(props.webhookPlatformOwner) && props.webhookPlatformOwner !== PLATFORM_NAME;

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      title={`Configuración de conexión ${props.name}`}
      maxWidth="lg"
      actions={
        <Button variant="contained" onClick={onSave} disabled={hooks.saving}>
          {hooks.saving ? 'Guardando...' : 'Guardar'}
        </Button>
      }
    >
      <Grid container size={12} flexDirection="column" spacing={2}>
        <Typography variant="body1" color={theme.palette.grey[500]} fontWeight="bold">
          Dirección de recolección
        </Typography>
        <Grid size={12}>
          <div className="px-4">
            <div className="flex items-center justify-center gap-4">
              <Typography
                color={
                  !hooks.useCustomAddress ? theme.palette.primary.main : theme.palette.grey[500]
                }
                fontWeight={!hooks.useCustomAddress ? 'bold' : 'normal'}
              >
                Usar dirección de Mercado Libre
              </Typography>
              <Switch
                checked={hooks.useCustomAddress}
                onChange={hooks.onToggleAddress}
                color="primary"
              />
              <Typography
                color={
                  hooks.useCustomAddress ? theme.palette.primary.main : theme.palette.grey[500]
                }
                fontWeight={hooks.useCustomAddress ? 'bold' : 'normal'}
              >
                Usar dirección diferente
              </Typography>
            </div>
            {!hooks.useCustomAddress && (
              <Typography variant="caption" color={theme.palette.grey[400]} mt={1} display="block">
                (Dirección predeterminada cargada automáticamente)
              </Typography>
            )}
            {hooks.useCustomAddress && (
              <Grid size={{ xxs: 12 }} mt={2}>
                <AddressAutocomplete
                  size="medium"
                  label="Nueva dirección de recolección"
                  placeholder="Dirección"
                  value={hooks.addressData.address}
                  country={hooks.addressData.country || 'MEX'}
                  onChange={hooks.handleChangeAddressText}
                  onSelectAddress={hooks.handleSelectAddress}
                />
                {hooks.addressData.address && (
                  <Typography
                    variant="caption"
                    color={theme.palette.grey[400]}
                    mt={0.5}
                    display="block"
                  >
                    {hooks.addressData.zipCode && `CP: ${hooks.addressData.zipCode}`}
                  </Typography>
                )}
              </Grid>
            )}
          </div>
        </Grid>

        <Typography variant="body1" color={theme.palette.grey[500]} mt={2} fontWeight="bold">
          Información de origen
        </Typography>
        <div className="w-full px-4">
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Nombre de tienda"
                value={hooks.storeInfo.storeName}
                onChange={(e) => hooks.onChangeStoreInfo('storeName', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Email de tienda"
                value={hooks.storeInfo.storeEmail}
                onChange={(e) => hooks.onChangeStoreInfo('storeEmail', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid size={12}>
              <MuiTelInput
                fullWidth
                size="small"
                label="Teléfono de tienda"
                defaultCountry="MX"
                onlyCountries={['MX', 'CO', 'CL', 'PE']}
                value={hooks.storeInfo.storePhone}
                onChange={hooks.onChangePhone}
                error={hooks.phoneError}
                helperText={hooks.phoneError ? 'Ingresa un número válido' : undefined}
              />
            </Grid>
          </Grid>
        </div>

        <Typography variant="body1" color={theme.palette.grey[500]} mt={2} fontWeight="bold">
          Configuraciones adicionales
        </Typography>
        <div className="flex flex-col gap-3 px-4">
          <FormControlLabel
            sx={{ gap: '1rem' }}
            control={
              <Checkbox
                onChange={() => hooks.onChangeCheckOptions('createOrders')}
                color="primary"
                name="createOrders"
                checked={hooks.checkOptions.createOrders}
              />
            }
            label={
              <Typography color={theme.palette.grey[500]}>Creación automática de orden</Typography>
            }
          />

          {hooks.checkOptions.createOrders && (
            <div className="w-full pl-2">
              <Autocomplete
                multiple
                id="auto-create-logistic-modes-parcel-select"
                options={AUTO_CREATE_LOGISTIC_MODES_OPTIONS}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                value={AUTO_CREATE_LOGISTIC_MODES_OPTIONS.filter((option) =>
                  hooks.autoCreateLogisticModes.includes(option.value),
                )}
                onChange={(_event, newValue) => {
                  hooks.onChangeLogisticModes(newValue.map((item) => item.value));
                }}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return <Chip key={key} label={option.label} size="small" {...tagProps} />;
                  })
                }
                renderOption={(optionProps, option) => {
                  const { key, ...otherProps } = optionProps;
                  return (
                    <li key={key} {...otherProps}>
                      <div className="flex flex-col py-1">
                        <Typography variant="body2" fontWeight="medium">
                          {option.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      </div>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Modos logísticos permitidos para creación automática"
                    placeholder={
                      hooks.autoCreateLogisticModes.length === 0
                        ? 'Selecciona modos logísticos'
                        : ''
                    }
                    helperText="Selecciona qué tipos de envíos de Mercado Libre deben crear guías automáticamente en 99minutos. Si no seleccionas ninguno, las órdenes solo se guardarán como pendientes para creación manual."
                  />
                )}
              />
            </div>
          )}

          {props.activeMlCount > 1 && (
            <Tooltip
              title={
                isPrimaryDisabled
                  ? 'Desactiva esta opción en la plataforma primaria actual antes de cambiarla'
                  : 'Si tienes varias plataformas activas, esta se priorizará para la creación automática de órdenes'
              }
              placement="right"
            >
              <span>
                <FormControlLabel
                  sx={{ gap: '1rem' }}
                  control={
                    <Checkbox
                      color="primary"
                      checked={hooks.isWebhookPrimary}
                      onChange={(e) => hooks.onChangeIsWebhookPrimary(e.target.checked)}
                      disabled={isPrimaryDisabled}
                    />
                  }
                  label={
                    <Typography
                      color={isPrimaryDisabled ? theme.palette.grey[400] : theme.palette.grey[500]}
                    >
                      Plataforma primaria
                    </Typography>
                  }
                />
              </span>
            </Tooltip>
          )}
        </div>
      </Grid>
    </Modal>
  );
};

export default MLParcelConfig;
